<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { ChevronDown, ChevronUp, Pin, Trash2, Zap } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import RelativeTime from '@/components/RelativeTime.vue'
import ActivityDetail from '@/components/comments/ActivityDetail.vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { getAvatarColor, getInitials } from '@/composables/useInitials'

export interface Comment {
  id: number
  comment_type: 'activity' | 'comment'
  author_name: string | null
  author_id: number | null
  author_avatar_url: string | null
  body: string
  event: string | null
  metadata: Record<string, unknown> | null
  pinned: boolean
  pinned_at: string | null
  created_at: string
  item_path: string
  can_delete: boolean
  can_pin: boolean
}

const props = defineProps<{ comment: Comment }>()

const isActivity = computed(() => props.comment.comment_type === 'activity')
const isExpandable = computed(() => isActivity.value && props.comment.event != null && props.comment.metadata != null)

const expanded = ref(false)

function formatBody(body: string) {
  return body.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

function togglePin() {
  router.patch(props.comment.item_path, { comment: { pinned: String(!props.comment.pinned) } }, { preserveScroll: true })
}

function deleteComment() {
  if (!confirm('Delete this comment?')) return
  router.delete(props.comment.item_path, { preserveScroll: true })
}

const avatarInitials = computed(() => getInitials(props.comment.author_name ?? '') || '?')
const avatarColor = computed(() => getAvatarColor(props.comment.author_name))
</script>

<template>
  <!-- Activity item — compact timeline style -->
  <div v-if="isActivity" class="flex items-start gap-2.5 py-2 opacity-70">
    <div class="bg-muted text-muted-foreground mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full">
      <Zap class="size-3" />
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-1.5">
        <p class="text-muted-foreground text-sm italic leading-relaxed" v-html="formatBody(comment.body)" />
        <button
          v-if="isExpandable"
          class="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
          :title="expanded ? 'Hide details' : 'Show details'"
          @click="expanded = !expanded"
        >
          <ChevronUp v-if="expanded" class="size-3" />
          <ChevronDown v-else class="size-3" />
        </button>
      </div>
      <ActivityDetail
        v-if="expanded && isExpandable && comment.event && comment.metadata"
        :event="comment.event"
        :metadata="comment.metadata"
      />
      <p class="text-muted-foreground/60 mt-0.5 text-sm">
        <RelativeTime :date="comment.created_at" />
      </p>
    </div>
  </div>

  <!-- Comment item -->
  <div v-else class="group flex gap-3 py-3">
    <!-- Avatar -->
    <div class="shrink-0">
      <Avatar class="size-8">
        <AvatarImage v-if="comment.author_avatar_url" :src="comment.author_avatar_url" :alt="comment.author_name ?? ''" />
        <AvatarFallback class="text-sm font-semibold" :class="avatarColor">{{ avatarInitials }}</AvatarFallback>
      </Avatar>
    </div>

    <!-- Bubble + meta -->
    <div class="min-w-0 flex-1">
      <!-- Header: name · time + actions -->
      <div class="mb-1 flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5 text-sm">
          <span class="text-foreground font-semibold">{{ comment.author_name ?? 'Unknown' }}</span>
          <span class="text-muted-foreground/50">·</span>
          <span class="text-muted-foreground"><RelativeTime :date="comment.created_at" /></span>
        </div>

        <!-- Actions — visible on group hover -->
        <div class="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <Button
            v-if="comment.can_pin"
            variant="ghost"
            size="icon"
            class="size-6"
            :class="comment.pinned ? 'text-primary opacity-100' : 'text-muted-foreground'"
            :title="comment.pinned ? 'Unpin' : 'Pin comment'"
            @click="togglePin"
          >
            <Pin class="size-3.5" />
          </Button>
          <Button
            v-if="comment.can_delete"
            variant="ghost"
            size="icon"
            class="text-muted-foreground hover:text-destructive size-6"
            title="Delete comment"
            @click="deleteComment"
          >
            <Trash2 class="size-3.5" />
          </Button>
        </div>
      </div>

      <!-- Body bubble -->
      <div class="bg-muted/50 border-border rounded-b-xl rounded-tr-xl border px-3 py-2 text-sm leading-relaxed" v-html="formatBody(comment.body)" />
    </div>
  </div>
</template>
